/**
 * Event contract — EventEnvelopeV1.
 *
 * Mirrors PRD §5 exactly. The backend publishes every real-time event wrapped
 * in this envelope; the discriminant is `event_type`, and `payload` is typed
 * per event via the discriminated union below.
 */

export type EventType =
  | 'session.started'
  | 'transcript.partial'
  | 'transcript.final'
  | 'translation.partial'
  | 'translation.final'
  | 'tts.chunk'
  | 'ops.alert'
  | 'session.ended';

export type OpsAlertLevel = 'info' | 'warn' | 'error';

/** Provider identifiers reported at session start. */
export interface SessionProviders {
  stt: string;
  translate: string;
  tts: string | null;
}

export interface SessionStartedPayload {
  name: string;
  tts_enabled: boolean;
  providers: SessionProviders;
}

export interface TranscriptPartialPayload {
  text: string;
}

export interface TranscriptFinalPayload {
  text: string;
  detected_lang: string;
}

export interface TranslationPartialPayload {
  text: string;
}

export interface TranslationFinalPayload {
  text: string;
  input_transcript: string;
}

export interface TtsChunkPayload {
  audio_b64: string;
  seq: number;
  mime: string;
}

export interface OpsAlertPayload {
  level: OpsAlertLevel;
  code: string;
  message: string;
}

export interface SessionEndedPayload {
  transcript_url: string;
  audio_url: string;
}

/** Maps each event_type to its payload shape. */
export interface EventPayloadMap {
  'session.started': SessionStartedPayload;
  'transcript.partial': TranscriptPartialPayload;
  'transcript.final': TranscriptFinalPayload;
  'translation.partial': TranslationPartialPayload;
  'translation.final': TranslationFinalPayload;
  'tts.chunk': TtsChunkPayload;
  'ops.alert': OpsAlertPayload;
  'session.ended': SessionEndedPayload;
}

/** Common envelope fields shared by every event (PRD §5). */
interface EventEnvelopeBase {
  event_id: string;
  session_id: string;
  /** Groups partial→final of the same segment. */
  segment_id: string;
  timestamp_ms: number;
  /** Detected (auto) or fixed (manual). */
  source_lang: string;
  /** Unique per session. */
  target_lang: string;
}

/** Discriminated union: narrow on `event_type` to get a typed `payload`. */
export type EventEnvelopeV1 = {
  [K in EventType]: EventEnvelopeBase & {
    event_type: K;
    payload: EventPayloadMap[K];
  };
}[EventType];

/** Runtime type guard — verifies a parsed object looks like an envelope. */
export function isEventEnvelopeV1(value: unknown): value is EventEnvelopeV1 {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.event_id === 'string' &&
    typeof v.session_id === 'string' &&
    typeof v.segment_id === 'string' &&
    typeof v.event_type === 'string' &&
    typeof v.timestamp_ms === 'number' &&
    typeof v.source_lang === 'string' &&
    typeof v.target_lang === 'string' &&
    typeof v.payload === 'object' &&
    v.payload !== null
  );
}
